import config from '../../config';
import moment from 'moment';
import idb from 'idb';
import { fetchJson as apiFetchJson } from '../../helpers/api';

const DB_VERSION = 3;

export const inspectionDb = idb.open('inspectionHistory', DB_VERSION, upgradeDB => {
	upgradeDB.createObjectStore('response_cache', { keyPath: 'key' });
	upgradeDB.createObjectStore('outbox', { keyPath: 'key' });
	upgradeDB.createObjectStore('log', { keyPath: 'key', autoincrement: true });
});

export function getStyleValue (property) {
	// Keep this in sync with the variables in index.css
	const styles = {
		'spacing-default': '16px',
		'spacing-half': '8px',
		'color-primary': '#26499E',
		'color-navbar': config.app_navbar_color || '#ff0000',
		'color-secondary': '#E0E0E0',
		'color-warning': '#FFF176',
		'color-text': 'rgba(0, 0, 0, 0.87)',
		'color-text-secondary': '#757575',
		'color-text-grey': '#b2b2b2',
		'color-divider': 'rgb(224, 224, 224)',
		'color-border': '#DEDEDE',
		'color-default': '#CFCFCF',
		'color-background': '#FAFAFA',
		'color-white': '#FFFFFF',
		'color-grey': '#EDEDED',
		'color-green': '#5BA10D',
		'color-orange': '#DB5E01',
		'color-red': '#F44336',
		'font-size-extra-large': '24px',
		'font-size-large': '18px',
		'font-size-default': '16px',
		'font-size-small': '14px',
		'font-size-smaller': '12px',
		'transition-default': 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
		'radius-default': '2px',
		'shadow-elevation-2': 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px',
		'shadow-elevation-3': 'rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px',
		'top-nav-height': '64px',
	};

	return styles[property];
}

export function raisedButtonRowStyles () {
	return {
		styleButton: {
			borderRadius: 0,
		},
		style: {
			borderRadius: 0,
			borderRight: `1px solid ${getStyleValue('color-border')}`,
			minWidth: '40px',
		},
		styleButtonFirst: {
			borderRadius: `${getStyleValue('radius-default')} 0 0 ${getStyleValue('radius-default')}`,
			border: 0,
		},
		styleFirst: {
			borderRadius: `${getStyleValue('radius-default')} 0 0 ${getStyleValue('radius-default')}`,
			borderRight: `1px solid ${getStyleValue('color-border')}`,
			minWidth: '40px',
		},
		styleButtonLast: {
			borderRadius: `0 ${getStyleValue('radius-default')} ${getStyleValue('radius-default')} 0`,
			border: 0,
		},
		styleLast: {
			borderRadius: `${getStyleValue('radius-default')} 0 0 ${getStyleValue('radius-default')}`,
			borderRight: 0,
			minWidth: '40px',
		},
		styleButtonIcon: {
			paddingLeft: 0,
			paddingRight: 0,
		},
	};
}

export function convertDMYtoYMD (dmyDate) {
	return new Date(moment(dmyDate, 'DD-MM-YYYY').format('YYYY-MM-DD'));
}

export function ucfirst (string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * @deprecated where possible use `fetchJson` funtion from `helpers/api`
 */
export function fetchJson (endpoint) {
	const request = `${config.api.url}/${endpoint}`;
	return fetch(request, {
		credentials: 'include',
	})
		.then(response => {
			if (response.ok) {
				return response.json();
			} else {
				if ((response.status === 401 || response.status === 403) && window.location.pathname !== '/login') { // not logged in
					window.location.href = '/login';
				} else {
					throw new Error(`${response.status}: ${response.statusText}`);
				}
			}
		}).catch(error => {
			return { error, request };
		});
}

export function scrollToElement (element, topPadding = 140) {
	if (element) {
		const elmRect = element.getBoundingClientRect();
		const bodyRect = document.body.getBoundingClientRect();
		const offset = (elmRect.top - bodyRect.top) - topPadding;
		window.scrollTo(0, offset);
	}
}

// For remarks, and triggering the outbox
export function sendJson (body, endpoint) {
	const url = `${config.api.url}/${endpoint}`;
	return fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		body: JSON.stringify(body),
	}).then((response) => {
		return response.json();
	}).catch((error) => {
		console.error(error);
		return { error, url };
	});
}

// For sending an inspection; note that it uses text/plain to avoid CORS preflight check
export function postJson (body, endpoint) {
	const url = `${config.api.url}/${endpoint}`;
	return fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'text/plain',
		},
		credentials: 'include',
		body: JSON.stringify(body),
	});
}

export function patchJson (body, endpoint) {
	const url = `${config.api.url}/${endpoint}`;
	return fetch(url, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		body: JSON.stringify(body),
	});
}

export function getStatusFromValue (value) {
	const valueMap = {
		0: '17',
		1: '7',
		2: '8',
		3: '9',
		4: '10',
		5: '11',
		6: '13',
		8: '14',
		9: '15',
		'AB': '6',
		'RV': '3',
		'VV': '2',
	};
	return valueMap[value];
}

export function getValueFromStatus (status) {
	const valueMap = {
		17: '0',
		7: '1',
		8: '2',
		9: '3',
		10: '4',
		11: '5',
		13: '6',
		14: '8',
		15: '9',
		6: 'AB',
		3: 'RV',
		2: 'VV',
	};
	return valueMap[status];
}

export function isValidYear (year) {
	const date = new Date();
	return year >= 1970 && year <= date.getFullYear() + 1;
}

/**
 * Writes a log line to IndexedDB
 * @TODO Implement sentry here; we want to know when logging failed, and send
 *       any errors to Sentry too -> https://docs.sentry.io/platforms/javascript/guides/react/
 *
 * Note that this function is duplicated in src/sw.js
 *
 * @param  {String} message
 * @param  {String} type    ['log', 'warn', 'error']
 * @return {Promise}
 */
export async function writeLog(message, type = 'log') {
	try {
		return await inspectionDb
			.then(db => {
				return db
					.transaction('log', 'readwrite')
					.objectStore('log')
					.put({ key: Date.now(), message, type });
			});
	} catch(error) {
		console.error(error);
	}
}

/**
 * Clears the client log
 *
 * @return {Promise}
 */
export async function clearLog() {
	try {
		return await inspectionDb
			.then(db => {
				return db
					.transaction('log', 'readwrite')
					.objectStore('log')
					.clear();
			});
	} catch(error) {
		console.error(error);
	}
}

export function getDutchExecutionDate(execution_date) {
	if (moment(execution_date).isValid()) {
		return moment(execution_date).format('DD-MM-YYYY');
	} else {
		return null;
	}
}

/**
 * Get translations object either from localStorage, or if it’s not there,
 * load it from remote API. If that fails, we have a broken interface :/
 *
 * @return {Object}
 */
export async function getTranslations() {
	const translationsFromLocalStore = getFromLocalStorage('t');
	if (Object.keys(translationsFromLocalStore).length === 0) {
		try {
			const response = await fetchJson('translations');
			if (response.error) {
				throw Error(response.error);
			} else {
				return response.data;
			}
		} catch(error) {
			console.warn('Translations could not be loaded.');
			console.error(error);
			return {};
		}
	} else {
		return translationsFromLocalStore;
	}
}

/**
 * Get lacks from API; they will be stored in localstorage in redux store
 * Note that remote items don’t need to be merged with local items: local items
 * that aren’t linked to an existing lack are stored in the inspection_part_lacks
 * property as a title+part_id, and as soon as the inspection syncs, they are
 * added to the remote database and will turn up in localstorage after this
 * function is called.
 *
 * During offline work, the newly created items will simply be strings in
 * localstorage that can be selected in the dropdown and work fine. As soon
 * as the network restores and inspections are synced, these 'title only'
 * lacks in localstorage become obsolete
 *
 * @return {Promise[]}
 */
export async function getLacks() {
	return apiFetchJson({ path: 'lacks' })
		.then(({ data }) => data)
		.catch((error) => {
			// @todo add to log
			// in App.js, this probably will result in an error that is added to the log
			console.warn('Lacks could not be loaded.');
			console.error(error);
			return [];
		});
}

/**
 * Get part types (for brand & serials) from API and merge with any existing
 * part types in localstorage
 *
 * @todo maybe merging isn't needed and/or makes things overly complex? See
 * comparable implementation for getLacks
 *
 * @return {Promise<Array>}
 */
export async function getPartTypes() {

	/**
	 * Fetch part types from BFF
	 *
	 * @return {Array}
	 */
	async function fetchPartTypes() {
		return apiFetchJson({ path: 'part-types' })
			.then(({ data }) => data)
			.catch((error) => {
				// @todo add to log
				console.warn('Part types could not be loaded.');
				console.error(error);
				return [];
			});
	}

	/**
	 * Merge part types from API with part types already present in localstorage
	 * Finds out if any items from API are unique; they will remain present
	 * in the newFromAPI array and be merged with the local array. If no
	 * new items come from the server, the newFromAPI array will be empty. If
	 * localstorage was empty, it will be filled with all items from the API.
	 *
	 * @param  {Array} fromLocalStorage
	 * @param  {Array} fromAPI
	 * @return {Array}
	 */
	function mergePartTypes(fromLocalStorage, fromAPI) {
		const newFromAPI = fromAPI.map((apiItem) => {
			if (!fromLocalStorage.some(localItem => localItem.id === apiItem.id)) {
				return apiItem;
			}
			return null;
		}).filter(i => i); // remove falsy values

		return [...fromLocalStorage, ...newFromAPI];
	}

	const partTypesLocal = getFromLocalStorage('BrandsAndTypes', []);
	const partTypesAPI = await fetchPartTypes();
	return mergePartTypes(partTypesLocal, partTypesAPI);
}

/**
 * Tries to parse an item stored in localStorage as JSON. If the item
 * isn’t found, returns a fallback value; if parsing fails, returns fallback
 * value too
 *
 * @param  {String} item
 * @return {Object}
 */
export function getFromLocalStorage(item, fallback = {}) {
	try {
		return JSON.parse(localStorage.getItem(item)) || fallback;
	} catch (error) {
		return fallback;
	}
}
